/** Tenant roles, ordered least to most privileged. */
export const TENANT_ROLES = ['reader', 'reviewer', 'author', 'admin', 'owner'] as const;
export type TenantRole = (typeof TENANT_ROLES)[number];

export const ROLE_RANK: Record<TenantRole, number> = {
  reader: 0,
  reviewer: 1,
  author: 2,
  admin: 3,
  owner: 4,
};

export const ROLE_LABELS: Record<TenantRole, string> = {
  reader: 'Reader',
  reviewer: 'Reviewer',
  author: 'Author',
  admin: 'Administrator',
  owner: 'Owner',
};

export const ROLE_DESCRIPTIONS: Record<TenantRole, string> = {
  reader: 'Can read published documents and their history.',
  reviewer: 'Can comment on and vote on change requests.',
  author: 'Can create and edit documents and raise change requests.',
  admin: 'Can manage templates, members, approval policies and settings.',
  owner: 'Full control including billing, deletion and ownership transfer.',
};

/** Lifecycle of a document as a whole. */
export const DOCUMENT_STATUSES = ['draft', 'in_review', 'published', 'archived'] as const;
export type DocumentStatus = (typeof DOCUMENT_STATUSES)[number];

/** Lifecycle of one version of a document. */
export const VERSION_STATES = ['draft', 'in_review', 'approved', 'published', 'superseded', 'rejected'] as const;
export type VersionState = (typeof VERSION_STATES)[number];

export const CHANGE_REQUEST_STATES = ['draft', 'open', 'approved', 'changes_requested', 'rejected', 'merged', 'withdrawn'] as const;
export type ChangeRequestState = (typeof CHANGE_REQUEST_STATES)[number];

export const REVIEW_DECISIONS = ['approve', 'request_changes', 'comment'] as const;
export type ReviewDecision = (typeof REVIEW_DECISIONS)[number];

export const CLASSIFICATIONS = ['public', 'internal', 'confidential', 'restricted'] as const;
export type Classification = (typeof CLASSIFICATIONS)[number];

export const EXPORT_FORMATS = ['markdown', 'html', 'pdf', 'docx', 'json', 'bundle'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

/** Terminal change-request states cannot transition further. */
export const TERMINAL_CR_STATES: ChangeRequestState[] = ['merged', 'rejected', 'withdrawn'];

export const CR_TRANSITIONS: Record<ChangeRequestState, ChangeRequestState[]> = {
  draft: ['open', 'withdrawn'],
  open: ['approved', 'changes_requested', 'rejected', 'withdrawn'],
  changes_requested: ['open', 'rejected', 'withdrawn'],
  approved: ['merged', 'changes_requested', 'rejected', 'withdrawn'],
  rejected: ['open'],
  merged: [],
  withdrawn: ['open'],
};

export function canTransitionChangeRequest(from: ChangeRequestState, to: ChangeRequestState): boolean {
  return CR_TRANSITIONS[from]?.includes(to) ?? false;
}
